// Philippine Time (UTC+8) helpers using Intl API for reliable timezone conversion
export function getNowPHT() {
  // Get current time components in Asia/Manila timezone
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const parts = formatter.formatToParts(now);
  const get = (type) => parts.find(p => p.type === type)?.value;
  
  return new Date(
    parseInt(get('year')),
    parseInt(get('month')) - 1,
    parseInt(get('day')),
    parseInt(get('hour')),
    parseInt(get('minute')),
    parseInt(get('second'))
  );
}

export function getTodayPHT() {
  // Use Intl API to get today's date in PHT reliably
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Manila',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return formatter.format(new Date()); // Returns YYYY-MM-DD format
}

export function getCurrentHourPHT() {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    hour: 'numeric',
    hour12: false
  });
  return parseInt(formatter.format(new Date()));
}

export function formatHour(hour) {
  if (hour === 0 || hour === 24) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
}

export function formatTimeRange(startHour, endHour) {
  return `${formatHour(startHour)}–${formatHour(endHour)}`;
}

export function formatDuration(hours) {
  if (hours === 1) return '1 hour';
  return `${hours} hours`;
}

export function formatPrice(amount) {
  return `₱${amount.toLocaleString()}`;
}

export function formatPricePerHour(rate) {
  return `₱${rate.toLocaleString()} per hour`;
}

export function formatDate(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  const todayStr = getTodayPHT();
  const today = new Date(todayStr + 'T00:00:00');
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.getTime() === today.getTime()) return 'Today';
  if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function formatDateFriendly(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  const todayStr = getTodayPHT();
  const today = new Date(todayStr + 'T00:00:00');
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.getTime() === today.getTime()) return 'Today';
  if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

export function getStatusColor(status) {
  switch (status) {
    case 'available': return 'bg-status-available text-court-green';
    case 'booked': return 'bg-status-booked text-gray-500';
    case 'open_play': return 'bg-status-open-play text-blue-800';
    case 'unavailable': return 'bg-status-unavailable text-red-700';
    default: return 'bg-gray-100 text-gray-500';
  }
}

export function getStatusLabel(status) {
  switch (status) {
    case 'available': return 'Available';
    case 'booked': return 'Booked';
    case 'open_play': return 'Open Play';
    case 'blocked': return 'Blocked';
    case 'unavailable': return 'Unavailable';
    case 'limited': return 'Limited';
    case 'fully_booked': return 'Fully Booked';
    // Payment status labels used by OperatorToday grid
    case 'paid': return 'Confirmed';
    case 'partial': return 'Partial';
    case 'proof_submitted': return 'Proof Sent';
    case 'pending': return 'Pending';
    default: return status;
  }
}

// Enhanced booking status label that considers time context
export function getBookingStatusLabel(booking, isToday, currentHour) {
  if (!booking) return '';
  if (booking.payment_status === 'paid') return 'Confirmed';
  if (booking.payment_status === 'partial') return 'Partial';
  if (booking.payment_status === 'proof_submitted') return 'Proof Sent';
  // Confirmed booking with pending payment (operator-created "Unpaid") — never expires
  if (booking.status === 'confirmed' && booking.payment_status === 'pending') return 'Unpaid';
  if (booking.payment_status === 'pending' && isToday && booking.start_hour <= currentHour) return 'Unpaid';
  return 'Pending';
}

export function getPaymentBadge(status) {
  switch (status) {
    case 'paid': return { label: 'Paid', className: 'bg-green-100 text-green-800' };
    case 'pending': return { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' };
    default: return { label: status, className: 'bg-gray-100 text-gray-600' };
  }
}
